import * as ko from "knockout";
import * as localisationHelper from "localisationHelper";

// Models
import InputsBase, { IInputsBase } from "Content/ts/Shared/Inputs/inputs-base";
import { IStandardButtonsBase } from "Content/ts/Shared/Buttons/StandardButtons/StandardButtonsBase";

// Components
import "Content/ts/Shared/BootstrapAlerts/AlertValidation/alert-validation";
import "Content/ts/Shared/Buttons/StandardButtons/Primary/button-primary";
import { EnumButtonStyle } from "Content/ts/Shared/Buttons/StandardButtons/Enums/EnumButtonStyle";
import { IResourceText } from "Content/ts/Shared/Localisation/Models/resourceText";

export interface IInputTextButtonParams extends IInputsBase, IStandardButtonsBase {
    ButtonStyle: EnumButtonStyle | KnockoutComputed<EnumButtonStyle>;
}

export const Deps = {
    LocalisationHelper: localisationHelper
};

export default class InputTextButtonGroup extends InputsBase implements IStandardButtonsBase {
    ClickEvent: Function;                                                           // Required

    ButtonText: string | KnockoutObservable<string>;                                // Required
    LocButtonText: IResourceText;

    FullLengthButton: boolean;                                                      // Optional
    ShowLoading: KnockoutObservable<boolean>;                                       // Optional
    DisplayCondition: KnockoutObservable<boolean>;                                  // Optional
    DisableCondition: KnockoutObservable<boolean>;                                  // Optional
    AriaControls: string | KnockoutObservable<string>                               // Optional
    AriaExpanded: boolean | KnockoutObservable<boolean>                             // Optional

    ButtonStyle: EnumButtonStyle | KnockoutComputed<EnumButtonStyle>                // Optional

    isPrimaryButton: KnockoutComputed<boolean>;
    isSuccessButton: KnockoutComputed<boolean>;
    isDangerButton: KnockoutComputed<boolean>;
    isInverseButton: KnockoutComputed<boolean>;
    isInverWhiteButton: KnockoutComputed<boolean>;

    Id: string;
    CssClasses: string;
    cssClasses: KnockoutComputed<string>;

    constructor(params: IInputTextButtonParams) {
        super(params);

        // Required params
        this.ClickEvent = params.ClickEvent;

        if (params.ButtonText && params.LocButtonText) {
            throw new Error("Please only provide text or loc text for the button");
        }

        if (!params.ButtonText && !params.LocButtonText) {
            throw new Error("Please provide text or loc text for the button");
        }

        this.ButtonText = (params.ButtonText)
            ? params.ButtonText
            : localisationHelper.getString(params.LocButtonText.Section, params.LocButtonText.Key);

        // Optional params
        this.Id = params.Id || "";
        this.CssClasses = params.CssClasses || "";

        this.FullLengthButton = params.FullLengthButton || false;
        this.DisableCondition = params.DisableCondition || ko.observable(false);
        this.DisplayCondition = params.DisplayCondition || ko.observable(true);
        this.ShowLoading = params.ShowLoading || ko.observable(false);
        this.ButtonStyle = params.ButtonStyle || 0 as EnumButtonStyle;

        if (this.FullLengthButton) {
            this.CssClasses += " btn-block";
        }
        this.AriaControls = params.AriaControls ?? undefined;
        this.AriaExpanded = params.AriaExpanded ?? undefined;

        this.setupComputeds();
    }

    setupComputeds(): void {
        this.isPrimaryButton = ko.computed(() => {
            return ko.unwrap(this.ButtonStyle) === 0 as EnumButtonStyle;
        });

        this.isSuccessButton = ko.pureComputed(() => {
            return ko.unwrap(this.ButtonStyle) === 1 as EnumButtonStyle;
        });

        this.isDangerButton = ko.pureComputed(() => {
            return ko.unwrap(this.ButtonStyle) === 2 as EnumButtonStyle;
        });

        this.isInverseButton = ko.pureComputed(() => {
            return ko.unwrap(this.ButtonStyle) === 3 as EnumButtonStyle;
        });

        this.isInverWhiteButton = ko.pureComputed(() => {
            return ko.unwrap(this.ButtonStyle) === 4 as EnumButtonStyle;
        });

        this.cssClasses = ko.pureComputed(() => this.CssClasses);
    };
}

const componentName = "input-text-button-group";
ko.components.unregister(componentName);
ko.components.register(componentName, {
    viewModel: InputTextButtonGroup,
    template: { require: `text!Content/ts/Shared/Inputs/TextButtonGroups/${componentName}.html` }
});